﻿module Meevo.Plugins.Register {
    'use strict';

    export class SaleGiftCardPayment extends SalePayment {
        giftCardId: string;
        giftCardNumber: string;
        giftCardSaleDate: Date;
        clientId: string;
        clientFirstName: string;
        clientLastName: string;
        clientDisplayName: string;
        giftCardType: Model.GiftCardType;
        giftCardTypeId: string;
        isPromo: boolean;
        originalAmount: number;
        giftCardSaleLineId: number;
        giftCardSaleId: number;
        giftCardSaleLocationId: number;

        constructor(props: SaleGiftCardPayment) {
            super('Millennium.Meevo.Register.Contracts.Model.SalePaymentGiftCard, Millennium.Meevo.Register.Contracts.Model');
            _.extend(this, props);
        }
    }

    export class SaleExternalGiftCardPayment extends SalePayment {
        giftCardNumber: string;
        giftCardSaleDate: Date;
        clientId: string;
        clientFirstName: string;
        clientLastName: string;
        clientDisplayName: string;
        paymentRequestId: string;

        constructor(props: SaleExternalGiftCardPayment) {
            super('Millennium.Meevo.Register.Contracts.Model.SalePaymentExternalGiftCard, Millennium.Meevo.Register.Contracts.Model');
            _.extend(this, props);
        }
    }

    export class GiftCardPaymentsSection extends Meevo.Plugins.Register.RegisterPaymentSectionBase {
        clientsWithGiftCards: any[] = [];
        private saleClientIds: string[] = [];
        selectedClient: Model.ClientListItem;
        includeExpired: boolean = false;
        canLookupGiftCardByClientName: boolean = false;
        canUseExpired: boolean = false;
        expiredGiftCardSecurityKeyPath: string;
        giftCardNumber: string;

        get $translate() { return this.registerContext.$translate; }
        get $q() { return this.registerContext.$q; }

        get dataService() { return this.registerContext.dataService; }
        get modalService() { return this.registerContext.modalService; }
        get securityService() { return this.registerContext.securityService; }
        get busyService() { return this.registerContext.busyService; }
        get registerGiftCardService() { return this.registerContext.registerGiftCardService; }

        constructor(registerContext: Meevo.Plugins.Register.RegisterContext) {
            super(RegisterSectionTypeEnum.GiftCardPayment, registerContext, 'giftCardPayments', Register.RegisterSectionCatTypeEnum.Payment);

            this.securityService.checkSecurityKey(Meevo.Components.Common.SecurityKeys.REGISTER_REGISTER_ACTION_LOOKUPGIFTCARDBYCLIENTNAME).then(result => {
                this.canLookupGiftCardByClientName = (result === Meevo.Model.SecurityAccessEnum.Grant);
            });
            this.securityService.checkSecurityItem(Meevo.Components.Common.SecurityKeys.REGISTER_REGISTER_ACTION_USEEXPIREDGIFTCARD).then(result => {
                this.canUseExpired = (result.securityAccess === Meevo.Model.SecurityAccessEnum.Grant);
                this.expiredGiftCardSecurityKeyPath = result.securityKeyPath;
            });
        }

        private isNonLiabilityPromo(giftCard: any): boolean {
            return !!giftCard && giftCard.isPromo && !giftCard.isPromoLiability;
        }

        public addGiftCards(giftCards, isManual: boolean, isEntered: boolean = false) : void {
            let clientIds = Object.keys(_(giftCards).groupBy('clientId'));
            clientIds.forEach((clientId) => {
                let clientIdResolved = clientId.toLowerCase() === "undefined" ? undefined : clientId;
                let clientWithGiftCards: any = _(giftCards).filter(giftCard => giftCard.clientId === clientIdResolved);
                let existingClient = _(this.clientsWithGiftCards).findWhere({ id: clientIdResolved });
                if (!existingClient) {
                    existingClient = {
                        id: clientIdResolved,
                        giftCards: [],
                        isManual: isManual
                    }

                    existingClient.displayName = clientIdResolved
                        ? `${clientWithGiftCards[0].clientLastName}, ${clientWithGiftCards[0].clientFirstName}`
                        : this.$translate.instant('LABEL_ANONYMOUS_OTHER');

                    this.clientsWithGiftCards.push(existingClient);
                }
                this.addMissingGiftCards(existingClient, clientWithGiftCards, isEntered);
                existingClient.giftCards = this.sort(existingClient.giftCards);
                // if was entered but for some reason a card wasn't selected, go through normal selection process. External GC won't have an Id but would still be selected
                if (!isEntered || existingClient.selectedGiftCard.length == 0) {
                    this.selectGiftCardForClient(existingClient, true);
                }
            });
        }

        private addMissingGiftCards(existingClient, giftCards, isEntered): void {
            giftCards = this.sort(giftCards);

            giftCards.forEach((currentGiftCard) => {
                if (currentGiftCard.clientId != existingClient.id) {
                    return;
                }

                let existingGiftCard = existingClient.giftCards.find(clientGC => {
                    if (currentGiftCard.isExternal) {
                        return clientGC.giftCardNumber === currentGiftCard.giftCardNumber;
                    }
                    return clientGC.id === currentGiftCard.id;
                });

                if (!existingGiftCard) 
                {
                    currentGiftCard.originalAmount = currentGiftCard.remainingAmount;
                    currentGiftCard.redeemAmount = 0;

                    existingClient.giftCards.push(currentGiftCard);

                    if (!currentGiftCard.isExpired) {
                        existingClient.hasNonExpired = true;
                    }
                    existingGiftCard = currentGiftCard;
                }
                // if gift card number was entered, want to make sure its selected in list
                if (isEntered) {
                    existingClient.selectedGiftCard = existingGiftCard;
                    this.closeGiftCardSelector(existingClient);
                }
            });
        }

        selectGiftCardForClient(client, preserveSelected = false) {
            if (preserveSelected && !!client.selectedGiftCard && _(client.giftCards).find(clientGC => {
                if (client.selectedGiftCard.isExternal) {
                    return clientGC.giftCardNumber === client.selectedGiftCard.giftCardNumber;
                }
                return clientGC.id === client.selectedGiftCard.id;
            })) {
                return;
            }

            var selectedGiftCard = _(client.giftCards).find((giftCard) => {
                return giftCard.remainingAmount > 0 && (this.includeExpired || !giftCard.isExpired || !client.hasNonExpired);
            });
            if (selectedGiftCard) {
                client.selectedGiftCard = selectedGiftCard;
                this.closeGiftCardSelector(client);
            }
            return selectedGiftCard;
        }

        sort(giftCards) {
            return _(giftCards).chain()
                .sortBy((gc: any) => gc.endDate)
                .sortBy((gc: any) => gc.startDate)
                .sortBy((gc: any) => gc.isExpired)
                .value();
        }

        giftCardNumberChange = _.debounce((event: JQueryEventObject) => { this.giftCardNumberChangeWorker(event); }, 200, true);

        giftCardNumberChangeWorker(event: JQueryEventObject) {
            if (event && event.type == 'keydown') {
                let htmlElem = (event.srcElement as HTMLElement) || (event.target as HTMLElement);
                htmlElem.blur();
                return;
            }

            if (this.giftCardNumber && String(this.giftCardNumber).trim() != '') {
                let request: any = {
                    prefix: this.giftCardNumber
                };
                this.busyService.start();
                this.dataService.customMethodData(`/api/Tenant/GiftCardExternalBin/CheckBin`,
                        'POST',
                        request)
                    .then((response: any) => {
                        if (!_.isEmpty(response.data.prefix)) {
                            this.dataService
                                .customMethodData(`/api/GiftCardExternalBin/CheckBalance`,
                                    'POST',
                                    { giftCardNumber: this.giftCardNumber })
                                .then((response: any) => {
                                    if (response.data.processingStatus === Meevo.Model.ProcessingStatusEnum.Success) {
                                        this.addGiftCards([{
                                                    giftCardNumber: this.giftCardNumber,
                                                    remainingAmount: response.data.balanceAmount,
                                                    isExternal: true                                                    
                                            }],
                                            true, true);
                                    } else {
                                        this.modalService.alert("MESSAGE_PROMO_GIFT_CARD_NOT_FOUND");
                                    }
                                })
                                .finally(() => {
                                    this.busyService.stop();
                                });
                        } else {
                            request = {
                                pageNumber: 0,
                                itemsPerPage: 10000,
                                criteria: {
                                    giftCardNumber: this.giftCardNumber,
                                    includeExpired: !this.canUseExpired || this.includeExpired
                                }
                            };
                            this.dataService.getListPaged('clientgiftcard', request)
                                .then((response: any) => {
                                    if (_(response).any()) {

                                        if (!this.canUseExpired && response.filter(card => !card.isExpired).length < 1) {
                                            this.securityService.showDenyWarning(this.expiredGiftCardSecurityKeyPath,
                                                Components.Common.SecurityKeys.REGISTER_REGISTER_ACTION_USEEXPIREDGIFTCARD);
                                        } else {
                                        let allowedGiftCards = _(response).reject((card: any) =>
                                            !this.registerGiftCardService.checkGCUsageRestriction(card, this.canUseExpired) || this.isNonLiabilityPromo(card));
                                        if (_(allowedGiftCards).any())
                                            this.addGiftCards(allowedGiftCards, true, true);
                                        }
                                    } else {
                                        this.modalService.alert("MESSAGE_PROMO_GIFT_CARD_NOT_FOUND");
                                    }
                                })
                                .finally(() => {
                                    this.busyService.stop();
                                });
                        }
                    });
            } else {
                let clientIds = this.clientsWithGiftCards.map(client => client.id);
                this.addClients(clientIds, true);
            }
        }

        includeExpiredChange() {
            this.clientsWithGiftCards.forEach(client => {
                if (!this.includeExpired && client.selectedGiftCard.isExpired) {
                    let nonExpiredGiftCard = _(client.giftCards).findWhere({isExpired: false});
                    if (nonExpiredGiftCard) {
                        client.selectedGiftCard = nonExpiredGiftCard;
                        this.closeGiftCardSelector(client);
                    }
                }
            });
        }
        
        hasItems() {
            return (this.includeExpired && _(this.clientsWithGiftCards).any()) || _(this.clientsWithGiftCards).findWhere({hasNonExpired: true}) != undefined;
        }

        closeGiftCardSelector(client) {
            let balanceDue = this.registerContext.getBalanceDue(false);
            this.setRedeemAmount(client.selectedGiftCard, balanceDue);
        }

        private setRedeemAmount(giftCard, balanceDue) {
            if (giftCard.isPromo) {
                giftCard.redeemAmount = giftCard.originalAmount;
            } else {
                let existingPaymentLine = this.getPaymentLineByClientGiftCard(giftCard);
                if (!existingPaymentLine) {
                    giftCard.redeemAmount = balanceDue >= giftCard.remainingAmount ? giftCard.remainingAmount : balanceDue;
                } else {
                    giftCard.redeemAmount = existingPaymentLine.amount;
                }
            }
        }

        paymentLineRemoved(giftCardPayment: Model.SaleGiftCardPayment) {
            this.clientsWithGiftCards.forEach((client) => {
                let giftCard = this.getClientGiftCardById(client.giftCards, giftCardPayment.giftCardId);
                if (giftCard) {
                    giftCard.remainingAmount = giftCard.originalAmount;
                    giftCard.redeemAmount = giftCard.isPromo ? giftCard.originalAmount : 0;
                    return;
                }
            });
            
            this.refreshRedeemAmounts();
        }

        canApply(giftCard) {
            var existingPaymentLine = this.getPaymentLineByClientGiftCard(giftCard);
            return !existingPaymentLine || existingPaymentLine.amount != giftCard.redeemAmount;
        }

        processExternal(client) {
            let defer = this.$q.defer();

            if (client.selectedGiftCard.isExternal) {
                let paymentLine = this.getPaymentLineByClientGiftCard(client.selectedGiftCard) as SaleExternalGiftCardPayment;
                const url = `/api/Drawer/${this.registerContext.selectedDrawer.id}/PaymentRequest`;

                const request = {
                    paymentRequest: {
                        giftCardNumber: client.selectedGiftCard.giftCardNumber,
                        amount: client.selectedGiftCard.redeemAmount,
                        tempId: paymentLine.tempId,
                        id: paymentLine.paymentRequestId
                    },
                    sale: this.registerContext.sale
                };

                this.dataService.customMethodData(url, paymentLine.paymentRequestId ? 'PUT' : 'POST', request)
                    .then((response) => {
                        let paymentRequest = response.data;
                        if (paymentRequest.onHoldId) {
                            this.registerContext.setSaleOnHoldId(paymentRequest.onHoldId, paymentRequest.holdType);
                            this.registerContext.updateSelfPaymentWithOnHoldId(paymentRequest.onHoldId, false);
                        }
                        if (paymentRequest.paymentRequestStatus === Model.PaymentRequestStatusEnum.Approved) {
                            let saleExternalGiftCardPayment = paymentLine as SaleExternalGiftCardPayment;
                            saleExternalGiftCardPayment.paymentRequestId = paymentRequest.id;
                            if (paymentRequest.amount < paymentLine.amount) {
                                paymentLine.amount = paymentRequest.amount;
                                client.selectedGiftCard.redeemAmount = paymentRequest.amount;
                            }

                            defer.resolve();
                        } else {
                            if (paymentRequest.creditCardProcessorStatus) {
                                let message = this.$translate.instant(paymentRequest.creditCardProcessorStatus.message);
                                if (message === paymentRequest.creditCardProcessorStatus.message) {
                                    message = this.$translate.instant("MESSAGE_CC_STATUS_UNKNOWN") + ` (${paymentRequest.creditCardProcessorStatus.message})`;
                                }
                                this.modalService.alert(message, Components.Modal.ModalTranslationModes.DoNotTranslate);
                            }

                            this.registerContext.removeTicketPaymentLine(paymentLine);
                            this.paymentLineRemoved(paymentLine);

                            defer.reject();
                        }

                    })
                    .catch(() => {
                        this.registerContext.removeTicketPaymentLine(paymentLine);
                        this.paymentLineRemoved(paymentLine);

                        defer.reject();
                    });
            } else {
                defer.resolve();
            }

            return defer.promise;
        }

        apply(client) {
            let finish = () =>
            {
                client.selectedGiftCard.redeemAmount = client.selectedGiftCard.redeemAmount >
                    client.selectedGiftCard.originalAmount
                    ? client.selectedGiftCard.originalAmount
                    : client.selectedGiftCard.redeemAmount;

                this.payment = this.getPaymentLineByClientGiftCard(client.selectedGiftCard);

                if (this.payment) {
                    if (this.payment.isPending) {
                        this.modalService.alert("MESSAGE_GIFT_CARD_PENDING_PROCESSING");
                        return;
                    }
                } else {
                    this.payment = client.selectedGiftCard.isExternal
                        ? new SaleExternalGiftCardPayment(<SaleExternalGiftCardPayment>{
                            giftCardNumber: client.selectedGiftCard.giftCardNumber,
                            giftCardSaleDate: client.selectedGiftCard.saleDate,
                            clientId: client.selectedGiftCard.clientId,
                            clientFirstName: client.selectedGiftCard.clientFirstName,
                            clientLastName: client.selectedGiftCard.clientLastName,
                            clientDisplayName: client.displayName,
                            paymentTypeId: _.findWhere(this.registerContext.paymentTypes, { paymentSubCategory: Model.PaymentSubCategoryEnum.ThirdPartyGiftCard }).id
                        })
                        : new SaleGiftCardPayment(<SaleGiftCardPayment>_.extend({
                                    giftCardId: client.selectedGiftCard.id,
                            giftCardSaleDate: client.selectedGiftCard.saleDate,
                            giftCardSaleId: client.selectedGiftCard.saleId,
                            giftCardSaleLineId: client.selectedGiftCard.saleLineId,
                            giftCardSaleLocationId: client.selectedGiftCard.saleLocationId,
                                    clientDisplayName: client.displayName,
                            paymentTypeId: _.findWhere(this.registerContext.paymentTypes, { paymentSubCategory: Model.PaymentSubCategoryEnum.GiftCard }).id,
                            skipPreserve: client.selectedGiftCard.isPromo
                                },
                                _.pick(client.selectedGiftCard, 'giftCardNumber', 'clientId', 'clientFirstName', 'clientLastName', 'giftCardTypeId', 'isPromo', 'originalAmount', 'promoServiceDisplayName'))
                        );
                }

                this.payment.paymentCategory = Model.PaymentCategoryEnum.GiftCard;
                this.payment.amount = client.selectedGiftCard.redeemAmount; // For promo, amount determined on server
                this.payment.tempId = Guid.newGuid().toString();

                this.busyService.start();
                this.payment.isPending = true;

                super.addPayment(false);

                this.processExternal(client)
                    .then(() => {
                        client.selectedGiftCard.remainingAmount = client.selectedGiftCard.originalAmount - client.selectedGiftCard.redeemAmount;
                        this.refreshRedeemAmounts();
                    })
                    .finally(() => {
                        this.payment.isPending = false;
                        this.busyService.stop();
                        if (!(<Model.SaleGiftCardPayment>this.payment).isPromo && this.registerContext.checkIfPaymentIsEnough()) {
                            this.registerContext.finishSection();
                        }
                        else if (!this.existsClientWithRemainingAmount()){
                            this.selectSectionPostAdd();
                        }
                        else {
                            this.selectGiftCardForClient(client);
                        }
                        this.payment = null;
                    });
            }

            if (client.selectedGiftCard.isExpired) {
                let message = this.$translate.instant('MESSAGE_REDEEM_EXPIRED_GIFT_CARD',
                    {
                        expirationDate: moment(client.selectedGiftCard.endDate).format('ddd M/D/YYYY')
                    });
                this.modalService.confirmHandler(message, Components.Modal.ModalTranslationModes.DoNotTranslate)
                    .then(proceed => {
                        if (proceed) {
                            finish();
                        }
                    });
            } else {
                finish();
            }
        }

        private existsClientWithRemainingAmount() {
            return _(this.clientsWithGiftCards).find((client) => _(client.giftCards).find((giftCard) => giftCard.remainingAmount > 0))
        }

        private refreshRedeemAmounts() {
            let balanceDue = this.registerContext.getBalanceDue(false);
            this.clientsWithGiftCards.forEach(client => {
                this.setRedeemAmount(client.selectedGiftCard, balanceDue);
            });
        }

        private removeClients(saleClientIds) {
            for (var i = this.clientsWithGiftCards.length - 1; i >= 0; i--) {
                if (!_(saleClientIds).contains(this.clientsWithGiftCards[i].id) && !this.clientsWithGiftCards[i].isManual) {
                    this.clientsWithGiftCards.splice(i, 1);
                }
            }
        }

        private addClients(newClientIds, isManual: boolean) {
            if (_(newClientIds).any()) {
                let request: any = {
                    pageNumber: 0,
                    itemsPerPage: 1000,
                    criteria: {
                        clientIds: newClientIds
                    }
                }
                this.dataService.getListPaged('clientgiftcard', request)
                    .then((response: any) => {
                        let allowedGiftCards = _(response).reject((card: any) => !this.registerGiftCardService.checkGCUsageRestriction(card) || this.isNonLiabilityPromo(card));
                        this.addGiftCards(allowedGiftCards, isManual);
                    });
            }
        }

        clientChanged() {
            if (this.selectedClient) {
                this.addClients([this.selectedClient.id], true);
                this.selectedClient = null;
            }
        }

        clear() {
            this.clientsWithGiftCards = [];
            this.saleClientIds = [];
        }

        updateClients() {
            let saleClientIds = _(this.registerContext.sale.saleLines).chain().where({ clientType: Model.ClientTypeEnum.Client }).pluck('clientId').unique().value();

            let newSaleClientIds: string[] = [];
            saleClientIds.forEach((saleClientId) => {
                if (!_(this.saleClientIds).contains(saleClientId)) {
                    newSaleClientIds.push(saleClientId);
                }
            });
            this.saleClientIds = saleClientIds;

            this.removeClients(saleClientIds);
            this.addClients(newSaleClientIds, false);
        }

        refreshRemainingAmounts() {
            this.clientsWithGiftCards.forEach(client => {
                client.giftCards.forEach(giftCard=> {
                    if (!this.getPaymentLineByClientGiftCard(giftCard)) {
                        giftCard.remainingAmount = giftCard.originalAmount;
                    }
                });
            });
        }

        reset() {
            this.giftCardNumber = undefined;
            this.refreshRemainingAmounts();
            this.updateClients();
            this.refreshRedeemAmounts();
        }

        getClientGiftCardById(cards: any[], id) {
            return _(cards).findWhere({id: id});
        }

        getPaymentLineByClientGiftCard(giftCard) {
            if (giftCard.isExternal) {
                return _(this.registerContext.sale.paymentLines).findWhere({ giftCardNumber: giftCard.giftCardNumber });
            }
            return _(this.registerContext.sale.paymentLines).findWhere({ giftCardId: giftCard.id });
        }
    }
}